import { atom, computed } from 'nanostores';
import { toast } from 'react-toastify';
import { pricingStore } from '~/lib/stores/pricing/pricing';
import type { PricingMembershipState } from '~/lib/stores/pricing/types';

import { userStore } from '~/lib/stores/user';
import type { OrganizationData } from '~/types/organization';
import { hmrPropertyFactory } from '~/utils/hmr';
import { teamsPricingList, type Pricing } from '~/utils/pricing';

const hmr = hmrPropertyFactory('TeamPricingPlansStore');

class TeamPricingSettingsStore {
  @hmr()
  selectedOrg = atom<OrganizationData | null>(null);

  membership = atom<PricingMembershipState | null>(null);

  selectedPricing = atom<Pricing>(teamsPricingList[0]);

  activePricing = atom<Pricing | null>(null);

  isLoading = atom(false);

  nextHigherTierPricing = computed([this.activePricing], (activePricing) => {
    if (!activePricing) {
      return null;
    }

    const nextTier = teamsPricingList.find((pricing) => pricing.tier === activePricing.tier + 1);

    return nextTier ?? null;
  });

  async upgradeToNextTier() {
    const nextTierPricing = this.nextHigherTierPricing.get();

    if (!nextTierPricing) {
      toast.error('You are already on the highest tier');
      return;
    }

    const organization = this.selectedOrg.get();

    if (!organization) {
      toast.error('Could not find the organization to change plan');
      return;
    }

    pricingStore.updatePlanScreenData.set({
      updateType: 'upgrade',
      pricing: nextTierPricing,
      interval: this.membership.get()?.subscription?.interval ?? 'monthly',
      organization,
    });

    return;
  }
}

export const teamPricingSettingsStore = new TeamPricingSettingsStore();

userStore.getActiveOrganization().subscribe((organization) => {
  if (!organization?.billing) {
    return;
  }

  teamPricingSettingsStore.selectedOrg.set(organization);

  const tier = organization.billing.tier ?? null;
  const nextTier = organization.billing.nextTier ?? null;

  teamPricingSettingsStore.membership.set({
    tier,
    nextTier,
    nextInterval: organization.billing.nextInterval,
    subscription: organization.billing.subscription,
  });

  if (tier) {
    teamPricingSettingsStore.activePricing.set(teamsPricingList.find((pricing) => pricing.tier === tier) ?? null);
  }
});

if (import.meta.env.DEV) {
  Object.assign(globalThis, { teamPricingSettingsStore });
}
